/**
 * Sélecteur de fenêtre d'analyse.
 *
 * Le réglage le plus lourd de conséquences de l'écran, et il faut que ça se voie : les
 * limites de contrôle, les règles de détection et les indices de capabilité portent tous
 * sur le segment choisi ici. Deux personnes comparant leurs Cpk sans savoir qu'elles
 * regardent des fenêtres différentes concluront que l'outil se trompe.
 *
 * D'où trois partis pris :
 *
 * 1. **La fenêtre courante est écrite en toutes lettres**, jamais sous-entendue.
 * 2. **Le défaut est « les 100 dernières »**, parce qu'une carte de contrôle renseigne
 *    sur l'état *courant* du procédé. Calculer des limites sur deux ans d'historique
 *    mélange des réglages et des lots de matière sans rapport : elles s'élargissent
 *    jusqu'à ne plus rien détecter.
 * 3. **Le segment manuel accepte les index ou les dates.** Huit fichiers d'usine sur dix
 *    ne portent aucune date exploitable ; imposer le temps rendrait l'outil inutilisable
 *    sur la majorité des cas réels.
 */

import { Clock, Hash, SlidersHorizontal } from "lucide-react";
import { useState } from "react";

import {
  DEFAULT_WINDOW,
  WINDOW_PRESETS,
  type AnalysisWindow,
} from "./mappingApi";
import { Button, Input, Select, cn, num } from "./ui/primitives";

export function WindowPicker({
  window: current,
  total,
  hasTimestamps,
  label,
  onChange,
}: {
  window: AnalysisWindow;
  /** Effectif de la plus longue série du fichier, pour dimensionner les bornes. */
  total: number;
  hasTimestamps: boolean;
  label: string;
  onChange: (next: AnalysisWindow) => void;
}) {
  const [manual, setManual] = useState(false);
  const [from, setFrom] = useState(1);
  const [to, setTo] = useState(Math.min(total, DEFAULT_WINDOW.size ?? 100));

  const isPreset = (size: number) =>
    (current.mode === "last" || current.mode === "first") &&
    current.size === size;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5">
      <span className="flex items-center gap-1.5 text-[0.78rem] font-medium">
        <SlidersHorizontal className="h-3.5 w-3.5 text-muted" />
        Calculs sur
      </span>

      <Select
        value={current.mode === "first" ? "first" : "last"}
        disabled={manual}
        onChange={(e) =>
          onChange({
            mode: e.target.value as "last" | "first",
            size: current.size ?? DEFAULT_WINDOW.size,
          })
        }
      >
        <option value="last">les dernières</option>
        <option value="first">les premières</option>
      </Select>

      <div className="flex gap-1">
        {WINDOW_PRESETS.map((size) => (
          <Button
            key={size}
            active={!manual && isPreset(size)}
            disabled={manual}
            onClick={() =>
              onChange({
                mode: current.mode === "first" ? "first" : "last",
                size,
              })
            }
          >
            {num(size)}
          </Button>
        ))}
        <Button
          active={!manual && current.mode === "all"}
          disabled={manual}
          onClick={() => onChange({ mode: "all" })}
        >
          tout
        </Button>
      </div>

      <Button
        active={manual}
        onClick={() => {
          const next = !manual;
          setManual(next);
          if (!next) onChange(DEFAULT_WINDOW);
        }}
      >
        <Hash className="h-3 w-3" /> segment précis
      </Button>

      {manual && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[0.75rem] text-muted">de</span>
          <Input
            type="number"
            min={1}
            max={total}
            value={from}
            className="w-20"
            onChange={(e) => setFrom(Math.max(1, Number(e.target.value)))}
          />
          <span className="text-[0.75rem] text-muted">à</span>
          <Input
            type="number"
            min={1}
            max={total}
            value={to}
            className="w-20"
            onChange={(e) => setTo(Math.max(1, Number(e.target.value)))}
          />
          <Button
            variant="primary"
            onClick={() =>
              onChange({
                mode: "range",
                start: Math.min(from, to),
                end: Math.max(from, to),
              })
            }
          >
            appliquer
          </Button>
          <span className="text-[0.72rem] text-faint">
            index de mesure, bornes incluses · 1 à {num(total)}
          </span>
        </div>
      )}

      <span
        className={cn(
          "ml-auto flex items-center gap-1.5 rounded-full px-2.5 py-1",
          "bg-accent-soft text-[0.75rem] font-medium text-accent",
        )}
      >
        {hasTimestamps && <Clock className="h-3 w-3" />}
        {label}
      </span>
    </div>
  );
}
